import { useState } from 'react';
import { useNavigate } from 'react-router';

import { apiErrorMessage } from '@shared/api/errors';
import type { SubQueryRow, TaskDetail } from '@shared/api/types';
import {
  BaseButton,
  BaseField,
  BaseMessage,
  BasePill,
  BaseSpinner,
  BaseTextarea,
  useToast,
} from '@shared/components/base';
import { COMMON_TEXT } from '@shared/constants/text';

import { useRaiseQueriesMutation, useTicketForTaskQuery } from '../api/inboxApi';
import { INBOX_TEXT } from '../constants';
import { stamp } from '../lib/inbox';

import { hasRecipients, NO_RECIPIENTS, type Recipients } from '../lib/recipients';

import { RecipientPicker } from './RecipientPicker';

/** The task drawer's Queries tab: this client's ticket for the task's service and year. */
export function TaskQueries({ task, canRaise }: { task: TaskDetail; canRaise: boolean }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { data, isLoading } = useTicketForTaskQuery(task.id);
  const [raise, raising] = useRaiseQueriesMutation();
  const [form, setForm] = useState(false);
  const [to, setTo] = useState<Recipients>(NO_RECIPIENTS);
  const [text, setText] = useState('');

  if (isLoading || !data) return <BaseSpinner />;
  const open = data.items.filter((i) => i.status === 'open');
  const resolved = data.items.filter((i) => i.status === 'resolved');

  const submit = async () => {
    if (!hasRecipients(to) || !text.trim()) {
      toast.info(
        hasRecipients(to) || text.trim()
          ? INBOX_TEXT.taskQueries.incompleteRowToast
          : INBOX_TEXT.taskQueries.emptyToast,
      );
      return;
    }
    const res = await raise({
      clientId: task.clientId,
      serviceKey: task.serviceKey,
      year: task.year,
      rows: [{ text, recipientIds: to.userIds, toClient: to.toClient }],
    });
    if (res.error) toast.error(apiErrorMessage(res.error));
    else {
      toast.success(res.data.message);
      setForm(false);
      setTo(NO_RECIPIENTS);
      setText('');
    }
  };

  const row = (q: SubQueryRow) => (
    <li key={q.id} className="flex items-start gap-3 border-b border-line py-2.5 last:border-b-0">
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[13px] whitespace-pre-wrap">{q.text}</span>
        <span className="text-[11.5px] text-text-3">
          {INBOX_TEXT.common.raisedBy} {q.raisedBy?.name ?? INBOX_TEXT.common.admin} {INBOX_TEXT.common.to}{' '}
          {[...q.recipients.map((r) => r.name), ...(q.toClient ? [COMMON_TEXT.fields.client] : [])].join(
            ', ',
          )}{' '}
          · {stamp(q.raisedAt)}
          {q.resolvedAt &&
            INBOX_TEXT.taskQueries.resolvedBy(
              q.resolvedBy?.name ?? INBOX_TEXT.common.admin,
              stamp(q.resolvedAt),
            )}
          {q.replies > 0 && INBOX_TEXT.taskQueries.repliesSuffix(q.replies)}
        </span>
      </span>
      <BaseButton
        size="sm"
        variant="secondary"
        onClick={() => {
          void navigate(`/inbox/queries?sq=${q.id}`);
        }}
      >
        {COMMON_TEXT.actions.view}
      </BaseButton>
    </li>
  );

  return (
    <div className="flex flex-col gap-3">
      {open.length > 0 ? (
        <BaseMessage tone="warn">
          {data.holdsTask
            ? INBOX_TEXT.taskQueries.openOnServiceHolds(open.length, data.serviceName)
            : INBOX_TEXT.taskQueries.openOnService(open.length, data.serviceName)}
        </BaseMessage>
      ) : (
        data.items.length === 0 && (
          <p className="text-[12.5px] text-text-3">{INBOX_TEXT.taskQueries.noQueries(data.serviceName)}</p>
        )
      )}

      {canRaise && !form && (
        <div>
          <BaseButton
            size="sm"
            variant="secondary"
            icon="pi pi-plus"
            onClick={() => {
              setForm(true);
            }}
          >
            {open.length ? INBOX_TEXT.taskQueries.raiseAnother : INBOX_TEXT.taskQueries.raiseOne}
          </BaseButton>
        </div>
      )}
      {form && (
        <section className="flex flex-col gap-3 rounded-app border border-line p-3">
          <RecipientPicker
            value={to}
            onChange={setTo}
            inline
            onDone={() => {
              document.getElementById('tq-text')?.focus();
            }}
          />
          <BaseField id="tq-text" label={INBOX_TEXT.taskQueries.queryLabel}>
            <BaseTextarea
              id="tq-text"
              value={text}
              rows={2}
              placeholder={INBOX_TEXT.common.answerPlaceholder}
              onChange={setText}
            />
          </BaseField>
          <div className="flex justify-end gap-2">
            <BaseButton
              variant="secondary"
              onClick={() => {
                setForm(false);
              }}
            >
              {COMMON_TEXT.actions.cancel}
            </BaseButton>
            <BaseButton loading={raising.isLoading} onClick={() => void submit()}>
              {INBOX_TEXT.taskQueries.raiseSubmit}
            </BaseButton>
          </div>
        </section>
      )}

      {open.length > 0 && (
        <section>
          <h4 className="mb-1 flex items-center gap-2 text-[11px] font-semibold tracking-[0.09em] text-text-3">
            {INBOX_TEXT.common.openQueries} <BasePill tone="warn">{open.length}</BasePill>
          </h4>
          <ul>{open.map(row)}</ul>
        </section>
      )}
      {resolved.length > 0 && (
        <section>
          <h4 className="mb-1 text-[11px] font-semibold tracking-[0.09em] text-text-3">
            {INBOX_TEXT.common.resolvedQueries}
          </h4>
          <ul>{resolved.map(row)}</ul>
        </section>
      )}
    </div>
  );
}
